import { compact } from 'es-toolkit/array';
import { isExtendedLoggingEnabled } from '@/lib/utils/logging';
import { getRecordValue, isRecord, toTrimmedNonEmptyStringOrUndefined } from '@/lib/utils/type-guards';

/**
 * Shared helpers for turning arbitrary error payloads into normalized structures and
 * human-readable guidance. Components should lean on these utilities instead of
 * inventing bespoke parsing so the recovery experience stays consistent across the app.
 */

export type NormalizedErrorKind =
  | 'validation'
  | 'authentication'
  | 'authorization'
  | 'not-found'
  | 'rate-limit'
  | 'conflict'
  | 'timeout'
  | 'network'
  | 'server'
  | 'unknown';

export interface NormalizedError {
  code?: string;
  details: string[];
  fallback: boolean;
  kind: NormalizedErrorKind;
  message: string;
  payload?: Record<string, unknown> | null;
  statusCode?: number;
}

interface NormalizeErrorOptions {
  fallbackMessage?: string;
}

type ErrorWithMeta = Error & {
  data?: unknown;
  response?: Response & { status?: number };
  status?: number;
  statusCode?: number;
  code?: string | number;
  name?: string;
};

type UnknownRecord = Record<string, unknown>;

const DEFAULT_FALLBACK_MESSAGE = 'Something went wrong';
const MAX_DETAIL_LENGTH = 320;

export type UserFacingErrorAction = 'load' | 'refresh' | 'update' | 'save' | 'delete' | 'create' | 'submit' | 'sync';

interface ActionVerbs {
  defaultMessage: (feature: string) => string;
  infinitive: string;
  progressive: string;
}

const ACTION_VERBS: Record<UserFacingErrorAction, ActionVerbs> = {
  load: {
    progressive: 'loading',
    infinitive: 'load',
    defaultMessage: (feature) => `We couldn't load ${feature}. Please try again.`,
  },
  refresh: {
    progressive: 'refreshing',
    infinitive: 'refresh',
    defaultMessage: (feature) => `We couldn't refresh ${feature}. Please try again.`,
  },
  update: {
    progressive: 'updating',
    infinitive: 'update',
    defaultMessage: (feature) => `We couldn't update ${feature}. Please try again.`,
  },
  save: {
    progressive: 'saving',
    infinitive: 'save',
    defaultMessage: (feature) => `We couldn't save ${feature}. Please try again.`,
  },
  delete: {
    progressive: 'deleting',
    infinitive: 'delete',
    defaultMessage: (feature) => `We couldn't delete ${feature}. Please try again.`,
  },
  create: {
    progressive: 'creating',
    infinitive: 'create',
    defaultMessage: (feature) => `We couldn't create ${feature}. Please try again.`,
  },
  submit: {
    progressive: 'submitting',
    infinitive: 'submit',
    defaultMessage: (feature) => `We couldn't submit ${feature}. Please try again.`,
  },
  sync: {
    progressive: 'syncing',
    infinitive: 'sync',
    defaultMessage: (feature) => `We couldn't sync ${feature}. Please try again.`,
  },
};

function resolveAction(action?: UserFacingErrorAction): ActionVerbs {
  if (!action) {
    return ACTION_VERBS.load;
  }
  return ACTION_VERBS[action] ?? ACTION_VERBS.load;
}

type ErrorMessageOverride =
  | string
  | ((parameters: { normalized: NormalizedError; feature: string; action: UserFacingErrorAction }) => string);

export interface BuildUserFacingErrorMessageOptions {
  action?: UserFacingErrorAction;
  fallbackMessage?: string;
  feature: string;
  overrides?: Partial<Record<NormalizedErrorKind, ErrorMessageOverride>>;
}

/**
 * Coerces an unknown error shape (thrown value, HTTP error, framework error, etc.) into
 * a deterministic {@link NormalizedError}. The result captures a best-effort message,
 * HTTP metadata, classification, and any structured payload we can safely surface.
 */
export function normalizeError(error: unknown, options?: NormalizeErrorOptions): NormalizedError {
  const fallbackMessage = options?.fallbackMessage ?? DEFAULT_FALLBACK_MESSAGE;

  if (!error) {
    return {
      message: fallbackMessage,
      kind: 'unknown',
      details: [],
      fallback: true,
      payload: null,
    } satisfies NormalizedError;
  }

  if (typeof error === 'string') {
    const trimmed = error.trim();
    return {
      message: trimmed.length > 0 ? trimmed : fallbackMessage,
      kind: 'unknown',
      details: [],
      fallback: trimmed.length === 0,
      payload: null,
    } satisfies NormalizedError;
  }

  const errorObject = error as Partial<ErrorWithMeta>;
  const payload = extractRecord(errorObject.data);
  const statusCode = extractStatusCode(errorObject);
  const code = extractCode(errorObject, payload);
  const baseMessage = selectPrimaryMessage(errorObject, payload) ?? fallbackMessage;
  const details = collectDetailMessages(payload);
  const enrichedMessage = appendDetails(baseMessage, details);

  const normalized: NormalizedError = {
    message: enrichedMessage,
    kind: classifyError({ errorObject, code, statusCode, payload }),
    details,
    fallback: baseMessage === fallbackMessage && !payload,
    payload,
  } satisfies NormalizedError;
  if (code) {
    normalized.code = code;
  }
  if (typeof statusCode === 'number') {
    normalized.statusCode = statusCode;
  }
  return normalized;
}

/**
 * Logs a normalized error to the console when extended logging is enabled, ensuring we
 * always capture the parsed context alongside the raw error object for debugging.
 */
export function logNormalizedError(
  context: string,
  error: unknown,
  normalized: NormalizedError,
  extras?: Record<string, unknown>
): void {
  if (!isExtendedLoggingEnabled()) {
    return;
  }

  const payload = normalized.payload ?? (isRecord(error) ? extractRecord(getRecordValue(error, 'data')) : null);

  console.error(`[${context}]`, 'Request failed', {
    message: normalized.message,
    code: normalized.code,
    statusCode: normalized.statusCode,
    kind: normalized.kind,
    details: normalized.details,
    fallback: normalized.fallback,
    payload,
    extras,
    error,
  });
}

/**
 * Builds user-facing copy for a normalized error. Callers provide the feature/action so
 * we can tailor the guidance while still honoring any specific overrides they need.
 */
export function buildUserFacingErrorMessage(
  normalized: NormalizedError | null,
  options: BuildUserFacingErrorMessageOptions
): string {
  const actionKey = options.action ?? 'load';
  const verbs = resolveAction(actionKey);
  const fallback = options.fallbackMessage ?? verbs.defaultMessage(options.feature);

  if (!normalized) {
    return fallback;
  }

  const override = options.overrides?.[normalized.kind];
  if (override) {
    return typeof override === 'function'
      ? override({ normalized, feature: options.feature, action: actionKey })
      : override;
  }

  switch (normalized.kind) {
    case 'authentication': {
      return `Your session expired while ${verbs.progressive} ${options.feature}. Refresh and sign in again to continue.`;
    }
    case 'authorization': {
      return `You don't have permission to access ${options.feature} right now. Refresh and sign in with a different account if needed.`;
    }
    case 'rate-limit': {
      return `We hit rate limits while ${verbs.progressive} ${options.feature}. Please try again in a few minutes.`;
    }
    case 'timeout': {
      return `The request timed out while ${verbs.progressive} ${options.feature}. Please try again.`;
    }
    case 'network': {
      return `We lost the connection while ${verbs.progressive} ${options.feature}. Check your network and try again.`;
    }
    case 'server': {
      return `Our servers ran into an issue while ${verbs.progressive} ${options.feature}. Please try again shortly.`;
    }
    case 'not-found': {
      return `We couldn't find ${options.feature}. It may have been moved or removed.`;
    }
    case 'conflict': {
      return `Another change conflicted while ${verbs.progressive} ${options.feature}. Refresh and try again.`;
    }
    case 'validation': {
      if (!normalized.fallback && normalized.message) {
        return normalized.message;
      }
      return fallback;
    }
    case 'unknown': {
      if (!normalized.fallback && normalized.message) {
        return normalized.message;
      }
      return fallback;
    }
    default: {
      return fallback;
    }
  }
}

const extractRecord = (value: unknown): UnknownRecord | null => (isRecord(value) ? value : null);

const extractStatusCode = (error: Partial<ErrorWithMeta>): number | null => {
  if (typeof error.statusCode === 'number') {
    return error.statusCode;
  }
  if (typeof error.status === 'number') {
    return error.status;
  }
  const responseStatus = error.response?.status;
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }
  const payload = extractRecord(error.data);
  if (!payload) {
    return null;
  }
  const payloadStatusCode = getRecordValue(payload, 'statusCode');
  if (typeof payloadStatusCode === 'number') {
    return payloadStatusCode;
  }
  const payloadStatus = getRecordValue(payload, 'status');
  return typeof payloadStatus === 'number' ? payloadStatus : null;
};

const extractCode = (error: Partial<ErrorWithMeta>, payload: UnknownRecord | null): string | null => {
  if (typeof error.code === 'string') {
    return error.code;
  }
  if (typeof error.code === 'number') {
    return String(error.code);
  }
  if (!payload) {
    return null;
  }
  const payloadCode =
    getRecordValue(payload, 'code') ??
    getRecordValue(payload, 'errorCode') ??
    getRecordValue(payload, 'type') ??
    getRecordValue(payload, 'reason');
  if (typeof payloadCode === 'string') {
    return payloadCode;
  }
  if (typeof payloadCode === 'number') {
    return String(payloadCode);
  }
  return null;
};

function selectPrimaryMessage(error: Partial<ErrorWithMeta>, payload: UnknownRecord | null): string | null {
  const candidates: unknown[] = [];
  if (payload) {
    candidates.push(
      getRecordValue(payload, 'error'),
      getRecordValue(payload, 'message'),
      getRecordValue(payload, 'detail'),
      getRecordValue(payload, 'description'),
      getRecordValue(payload, 'reason'),
      getRecordValue(payload, 'title')
    );
  }
  if (typeof error.message === 'string' && error.message.trim().length > 0) {
    candidates.push(error.message);
  }
  for (const candidate of candidates) {
    const text = toStringIfPresent(candidate);
    if (text) {
      return text;
    }
  }
  return null;
}

function collectDetailMessages(payload: UnknownRecord | null): string[] {
  if (!payload) {
    return [];
  }

  const detailSources: unknown[] = [];
  const issues = getRecordValue(payload, 'issues');
  if (Array.isArray(issues)) {
    detailSources.push(issues);
  }
  const errors = getRecordValue(payload, 'errors');
  if (Array.isArray(errors)) {
    detailSources.push(errors);
  } else if (isRecord(errors)) {
    detailSources.push(errors);
  }
  const extraKeys = ['details', 'hint', 'suggestion', 'action'] as const;
  for (const key of extraKeys) {
    const value = getRecordValue(payload, key);
    if (value !== undefined) {
      detailSources.push(value);
    }
  }

  const messages: string[] = [];
  const seen = new Set<string>();
  for (const source of detailSources) {
    for (const detail of flattenDetail(source, 0)) {
      const trimmed = detail.trim();
      if (trimmed.length === 0) {
        continue;
      }
      if (seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);
      messages.push(trimDetail(trimmed));
    }
  }
  return messages;
}

const TRAILING_PUNCTUATION_PATTERN = /[.:!?]$/;

function appendDetails(baseMessage: string, details: string[]): string {
  if (details.length === 0) {
    return baseMessage;
  }
  const suffix = details.join('; ');
  const normalizedBase = baseMessage.trim();
  if (suffix.length === 0) {
    return normalizedBase;
  }
  const hasTrailingPunctuation = TRAILING_PUNCTUATION_PATTERN.test(normalizedBase);
  return `${normalizedBase}${hasTrailingPunctuation ? '' : ':'} ${suffix}`;
}

function classifyError(arguments_: {
  errorObject: Partial<ErrorWithMeta>;
  code: string | null;
  statusCode: number | null;
  payload: UnknownRecord | null;
}): NormalizedErrorKind {
  const { errorObject, code, statusCode } = arguments_;
  const normalizedCode = code?.toUpperCase() ?? '';
  const name = (errorObject.name ?? '').toLowerCase();

  if (statusCode === 401 || normalizedCode.includes('AUTH')) {
    return 'authentication';
  }
  if (statusCode === 403 || normalizedCode.includes('FORBIDDEN')) {
    return 'authorization';
  }
  if (statusCode === 404 || normalizedCode.includes('NOT_FOUND')) {
    return 'not-found';
  }
  if (statusCode === 409 || normalizedCode.includes('CONFLICT')) {
    return 'conflict';
  }
  if (statusCode === 422 || normalizedCode.includes('VALIDATION')) {
    return 'validation';
  }
  if (statusCode === 429 || normalizedCode.includes('RATE_LIMIT')) {
    return 'rate-limit';
  }
  if (
    normalizedCode.includes('TIMEOUT') ||
    name === 'abortexception' ||
    name === 'aborted' ||
    name === 'timeouterror'
  ) {
    return 'timeout';
  }
  if (name === 'aborterror') {
    return 'timeout';
  }
  if (normalizedCode.includes('NETWORK') || name === 'networkerror') {
    return 'network';
  }
  if (typeof statusCode === 'number' && statusCode >= 500) {
    return 'server';
  }
  return 'unknown';
}

function flattenDetail(value: unknown, depth: number): string[] {
  if (depth > 3) {
    return [];
  }
  if (value === null || value === undefined) {
    return [];
  }
  if (typeof value === 'string') {
    return [value];
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenDetail(item, depth + 1));
  }
  if (isRecord(value)) {
    const message = toTrimmedNonEmptyStringOrUndefined(getRecordValue(value, 'message'));
    const rawPath = getRecordValue(value, 'path');
    const path = Array.isArray(rawPath)
      ? compact(
          rawPath.map((segment) =>
            typeof segment === 'string' || typeof segment === 'number' ? String(segment) : null
          )
        ).join('.')
      : undefined;
    if (message) {
      return [path ? `${message} (${path})` : message];
    }
    const nestedEntries = Object.entries(value)
      .map(([key, value_]) => {
        const nested = flattenDetail(value_, depth + 1);
        if (nested.length === 0) {
          return null;
        }
        return `${key}: ${nested.join(', ')}`;
      })
      .filter((entry): entry is string => entry !== null);
    return nestedEntries;
  }
  return [];
}

function toStringIfPresent(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function trimDetail(detail: string): string {
  if (detail.length <= MAX_DETAIL_LENGTH) {
    return detail;
  }
  return `${detail.slice(0, MAX_DETAIL_LENGTH)}…`;
}
